/**
 * Renders a pending interactive block (question or decision).
 * Positioned above the chat transcript to prevent typing while a question is pending.
 */
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../components/ui/button';
import type { PendingInteractiveBlock } from './ChatTranscript';
import { QuestionCard } from './QuestionCard';
import { DecisionButtons } from './DecisionButtons';

interface PendingBlockAreaProps {
  pendingBlock: PendingInteractiveBlock | null;
  /** Called when user dismisses the pending block */
  onDismiss?: () => void;
  /** Wrapper class overrides (default: padded with bottom border for above-transcript use) */
  className?: string;
}

export function PendingBlockArea({ pendingBlock, onDismiss, className }: PendingBlockAreaProps) {
  const { t } = useTranslation('common');

  if (!pendingBlock) return null;

  return (
    <div className={className ?? 'px-4 py-2 border-b border-border bg-muted/10'}>
      <div className="max-w-3xl mx-auto relative">
        {onDismiss && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-0 right-0 h-6 w-6 text-muted-foreground hover:text-foreground z-10"
            onClick={onDismiss}
            aria-label={t('app.dismiss')}
            data-testid="pending-block-dismiss"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        )}
        {pendingBlock.block.type === 'question' ? (
          <QuestionCard
            question={pendingBlock.block.question}
            options={pendingBlock.block.options}
            multiSelect={pendingBlock.block.multiSelect}
            allowOther
            interactive
            onSelect={pendingBlock.onSelect}
          />
        ) : (
          <DecisionButtons
            question={pendingBlock.block.question}
            options={pendingBlock.block.options}
            interactive
            onSelect={pendingBlock.onSelect}
          />
        )}
      </div>
    </div>
  );
}
